import { useApolloClient, useMutation } from '@apollo/client';
import { loader } from 'graphql.macro';
import { useCurrentUser } from '../../providers/UserProvider';
import { showToast } from '../../graphql/cache/modal';
import { toastVariant } from '../../constants/misc';

const roleUsersQuery = loader('./document_controller.roleUsers.graphql');
const addActionItemMutation = loader(
  './document_controller.addActionItem.graphql',
);
const addParticipantMutation = loader(
  './document_controller.addParticipant.graphql',
);

const toastTitle = 'Document Controller Action Item';

export const getRiskLabel = (risk) =>
  risk?.category || risk?.description || 'Untitled Risk';

export const useDocumentControllerActionItem = () => {
  const client = useApolloClient();
  const { company } = useCurrentUser();
  const [addActionItem] = useMutation(addActionItemMutation);
  const [addParticipant] = useMutation(addParticipantMutation);
  const roleId = company?.riskRegistrySettings?.documentControllerRoleId;

  // Users holding the company's default Document Controller role
  const getRoleControllerIds = async () => {
    if (!roleId) return [];
    try {
      const { data: { roles: [role] = [] } = {} } = await client.query({
        query: roleUsersQuery,
        fetchPolicy: 'network-only',
        variables: { roleId: `${roleId}` },
      });
      return role?.users?.map(({ id }) => id) || [];
    } catch (error) {
      showToast({
        title: 'Document Controllers',
        message: `Could not load document controllers: ${error.message}`,
        variant: toastVariant.warning,
      });
      return [];
    }
  };

  // One user is assigned directly, several are added as participants
  const createControllerActionItem = async ({
    userIds,
    variables,
    failureMessage,
  }) => {
    const ids = [...new Set(userIds || [])];
    if (!ids.length) return;
    const singleAssignee = ids.length === 1;
    const skipped = [];

    try {
      const {
        data: { addActionItem: actionItem },
      } = await addActionItem({
        variables: {
          ...variables,
          assigneeId: singleAssignee ? ids[0] : null,
        },
      });

      if (!singleAssignee) {
        for (const userId of ids) {
          try {
            await addParticipant({
              variables: { userId, actionItemId: actionItem.id },
            });
          } catch {
            skipped.push(userId);
          }
        }
      }
    } catch (error) {
      showToast({
        title: toastTitle,
        message: `${failureMessage}: ${error.message}`,
        variant: toastVariant.warning,
      });
      return;
    }

    if (skipped.length) {
      showToast({
        title: toastTitle,
        message: `${skipped.length} document controller(s) could not be added to the action item.`,
        variant: toastVariant.warning,
      });
    }
  };

  const createRiskCreatedActionItem = async ({ risk, userIds }) => {
    if (!risk?.id) return;
    const riskLabel = getRiskLabel(risk);
    await createControllerActionItem({
      userIds,
      failureMessage: 'Risk created, but the action item could not be created',
      variables: {
        title: `New Risk: ${riskLabel}${
          risk.externalId ? ` - ${risk.externalId}` : ''
        }`,
        description: `A new risk registry entry "${riskLabel}" has been created. Review the entry and update any controlled documents as required.`,
        workspaceId: null,
        riskRegistryId: risk.id,
      },
    });
  };

  return {
    getRoleControllerIds,
    createControllerActionItem,
    createRiskCreatedActionItem,
  };
};
